import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { User, AuthError } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithProvider: (provider: 'google' | 'github') => Promise<void>;
  signUp: (email: string, password: string, metadata?: { [key: string]: any }) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  error: null,

  signInWithEmail: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      if (data?.user) {
        set({ user: data.user });
        toast.success('Successfully signed in');
      }
    } catch (error) {
      const authError = error as AuthError;
      set({ error: authError.message });
      toast.error(authError.message);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  signInWithProvider: async (provider) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) throw error;
      if (!data) {
        throw new Error('No data returned from OAuth sign in');
      }
    } catch (error) {
      const authError = error as AuthError;
      set({ error: authError.message });
      toast.error(authError.message);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  signUp: async (email, password, metadata) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: metadata,
        },
      });
      
      if (error) throw error;
      if (data?.user) {
        toast.success('Please check your email to verify your account');
      }
    } catch (error) {
      const authError = error as AuthError;
      set({ error: authError.message });
      toast.error(authError.message);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  signOut: async () => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      set({ user: null });
      toast.success('Successfully signed out');
    } catch (error) {
      const authError = error as AuthError;
      set({ error: authError.message });
      toast.error(authError.message);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  resetPassword: async (email) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      toast.success('Password reset instructions sent to your email');
    } catch (error) {
      const authError = error as AuthError;
      set({ error: authError.message });
      toast.error(authError.message);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updatePassword: async (password) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success('Password updated successfully');
    } catch (error) {
      const authError = error as AuthError;
      set({ error: authError.message });
      toast.error(authError.message);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  setUser: (user) => set({ user, loading: false }),
  clearError: () => set({ error: null }),
}));